import { useState } from 'react'
import {
  Alert,
  AppBar,
  Avatar,
  Box,
  Chip,
  Divider,
  Drawer,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { ChatFlutuante } from '../components/ChatFlutuante'
import { SinoAlertas } from '../components/SinoAlertas'
import { DateRangeField } from '../components/DateRangeField'
import { BrandLogo } from '../components/BrandLogo'
import { Hicon } from '../components/Hicon'
import { MarcaFilterField } from '../components/MarcaFilterField'
import { DRAWER_WIDTH, SideNav } from '../components/SideNav'
import { TrocarSenhaDialog } from '../components/TrocarSenhaDialog'
import { InstalarAppDialog } from '../components/InstalarAppDialog'
import { useInstalarApp } from '../lib/instalar'
import { useAuth } from '../state/AuthContext'
import { useFiltros } from '../state/FiltrosContext'
import { useAppTheme } from '../state/ThemeContext'
import { THEME_OPTIONS, type ThemeId } from '../theme'
import { PERIODO_OPTIONS, type Periodo } from '../utils/periodo'

const APP_BAR_HEIGHT = '5rem'

export function AppLayout() {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isNavMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [navOpen, setNavOpen] = useState(false)
  const [userMenuEl, setUserMenuEl] = useState<null | HTMLElement>(null)
  const [trocandoSenha, setTrocandoSenha] = useState(false)
  const [instrucoesIOS, setInstrucoesIOS] = useState(false)
  const instalacao = useInstalarApp()
  const { themeId, setThemeId } = useAppTheme()
  const { user, logout, precisaTrocarSenha } = useAuth()
  const { periodo, setPeriodo, customRange, setCustomRange, marcas, marcasCarregando, marcasErro } =
    useFiltros()
  // Com acesso por usuário, quem acabou de ser criado entra sem marca nenhuma.
  const semMarcaLiberada = !marcasCarregando && !marcasErro && marcas.length === 0
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const emConfiguracoes = pathname.startsWith('/configuracoes')
  // Resumos são por semana e o chat escolhe o período pela pergunta: só a marca se aplica.
  const semPeriodo = pathname.startsWith('/resumos') || pathname.startsWith('/chat') || pathname.startsWith('/alertas')

  const drawerPaperSx = {
    width: DRAWER_WIDTH,
    boxSizing: 'border-box' as const,
    borderRight: 'none',
    backgroundImage: 'none',
  }

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        width: '100%',
        pt: 'env(safe-area-inset-top, 0px)',
      }}
    >
      {/* Sidebar desktop — fixa, não some ao rolar. */}
      <Drawer
        variant="permanent"
        open
        sx={{
          display: { xs: 'none', md: 'block' },
          width: DRAWER_WIDTH,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            ...drawerPaperSx,
            position: 'fixed',
            top: 0,
            left: 0,
            height: '100%',
          },
        }}
      >
        <SideNav />
      </Drawer>

      {/* Sidebar mobile — temporária */}
      <Drawer
        variant="temporary"
        open={navOpen}
        onClose={() => setNavOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          [`& .MuiDrawer-paper`]: drawerPaperSx,
        }}
      >
        <SideNav onNavigate={() => setNavOpen(false)} />
      </Drawer>

      <Box
        sx={{
          flexGrow: 1,
          minWidth: 0,
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          width: { xs: '100%', md: `calc(100% - ${DRAWER_WIDTH}px)` },
        }}
      >
        <AppBar position="sticky" elevation={0} sx={{ width: '100%', borderRadius: 0 }}>
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            justifyContent="space-between"
            useFlexGap
            flexWrap="nowrap"
            sx={{
              width: '100%',
              px: { xs: 1.5, sm: 2 },
              boxSizing: 'border-box',
              minHeight: APP_BAR_HEIGHT,
              height: APP_BAR_HEIGHT,
            }}
          >
            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
              {isNavMobile ? (
                <IconButton
                  color="inherit"
                  edge="start"
                  aria-label="Abrir menu"
                  onClick={() => setNavOpen(true)}
                  sx={{ flexShrink: 0 }}
                >
                  <Hicon name="menu" sx={{ fontSize: 24 }} />
                </IconButton>
              ) : null}
              {/* No desktop a marca já está no topo da barra lateral. */}
              {isNavMobile ? <BrandLogo size={isMobile ? 18 : 20} /> : null}
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
              <SinoAlertas />
              <TextField
                select
                size="small"
                label="Tema"
                value={themeId}
                onChange={(e) => setThemeId(e.target.value as ThemeId)}
                sx={{ minWidth: { xs: 100, sm: 120 } }}
              >
                {THEME_OPTIONS.map((opt) => (
                  <MenuItem key={opt.id} value={opt.id}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
              <Typography
                variant="body2"
                sx={{ display: { xs: 'none', sm: 'block' }, mr: 0.5, fontWeight: 500 }}
              >
                {user?.email || 'Usuário'}
              </Typography>
              <IconButton
                aria-label="Conta"
                onClick={(e) => setUserMenuEl(e.currentTarget)}
                sx={{ flexShrink: 0, p: 0.5 }}
              >
                <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                  <Hicon name="user" sx={{ fontSize: '1.125rem', color: 'common.white' }} />
                </Avatar>
              </IconButton>
              <Menu
                anchorEl={userMenuEl}
                open={Boolean(userMenuEl)}
                onClose={() => setUserMenuEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem disabled sx={{ opacity: '1 !important' }}>
                  <Typography variant="body2" color="text.secondary">
                    {user?.email}
                  </Typography>
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setUserMenuEl(null)
                    setTrocandoSenha(true)
                  }}
                >
                  <ListItemIcon>
                    <Hicon name="settings" sx={{ fontSize: '1.25rem' }} />
                  </ListItemIcon>
                  Trocar senha
                </MenuItem>
                {instalacao.temPedido || instalacao.precisaInstrucoes ? (
                  <MenuItem
                    onClick={() => {
                      setUserMenuEl(null)
                      if (instalacao.temPedido) instalacao.instalar()
                      else setInstrucoesIOS(true)
                    }}
                  >
                    <ListItemIcon>
                      <Hicon name="plus" sx={{ fontSize: '1.25rem' }} />
                    </ListItemIcon>
                    Instalar app
                  </MenuItem>
                ) : null}
                <Divider />
                <MenuItem
                  onClick={async () => {
                    setUserMenuEl(null)
                    await logout()
                    navigate('/login', { replace: true })
                  }}
                >
                  <ListItemIcon>
                    <Hicon name="logout" sx={{ fontSize: '1.25rem' }} />
                  </ListItemIcon>
                  Sair
                </MenuItem>
              </Menu>
              <TrocarSenhaDialog aberto={trocandoSenha} onFechar={() => setTrocandoSenha(false)} />
              {/* Senha provisória: pede a nova antes de qualquer coisa (fica aberto até trocar). */}
              <TrocarSenhaDialog aberto={precisaTrocarSenha} onFechar={() => undefined} obrigatorio />
              <InstalarAppDialog aberto={instrucoesIOS} onFechar={() => setInstrucoesIOS(false)} />
            </Stack>
          </Stack>
        </AppBar>

        {/* Filtros globais: período + marca. Valem para os painéis; nas telas de
            configuração não recortam nada, então não aparecem. */}
        {!emConfiguracoes ? (
          <Box sx={{ width: '100%', px: { xs: 1.5, sm: 2 }, pt: { xs: 1.5, sm: 2 }, boxSizing: 'border-box' }}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              useFlexGap
              flexWrap="wrap"
              sx={{
                px: { xs: 1.25, sm: 1.5 },
                py: 1.15,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: (t) =>
                  t.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(15, 118, 110, 0.18)',
                borderRadius: '8px',
                boxShadow: '0 1px 2px rgba(15, 23, 42, 0.03)',
              }}
            >
              {!semPeriodo ? (
                <>
              {isMobile ? (
                <TextField
                  select
                  size="small"
                  label="Período"
                  value={customRange ? 'custom' : periodo}
                  onChange={(e) => {
                    const v = e.target.value
                    if (v === 'custom') return
                    setPeriodo(v as Periodo)
                  }}
                  sx={{ flex: 1, minWidth: 120 }}
                >
                  {PERIODO_OPTIONS.map((opt) => (
                    <MenuItem key={opt.id} value={opt.id}>
                      {opt.label}
                    </MenuItem>
                  ))}
                  {customRange ? <MenuItem value="custom">Personalizado</MenuItem> : null}
                </TextField>
              ) : (
                <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                  {PERIODO_OPTIONS.map((opt) => {
                    const ativo = !customRange && periodo === opt.id
                    return (
                      <Chip
                        key={opt.id}
                        size="medium"
                        label={opt.label}
                        color={ativo ? 'primary' : 'default'}
                        variant={ativo ? 'filled' : 'outlined'}
                        onClick={() => setPeriodo(opt.id)}
                        sx={{
                          height: 36,
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          px: 0.5,
                          '& .MuiChip-label': { px: 1.5 },
                        }}
                      />
                    )
                  })}
                </Stack>
              )}
              <DateRangeField
                value={customRange}
                onApply={(r) => setCustomRange(r)}
                onClear={() => setCustomRange(null)}
                sx={isMobile ? { flex: 1, minWidth: 160 } : undefined}
              />
                </>
              ) : null}
              <MarcaFilterField sx={isMobile ? { flex: 1 } : { ml: { md: 'auto' } }} />
            </Stack>
          </Box>
        ) : null}

        <Box
          component="main"
          sx={{
            flex: 1,
            p: { xs: 1.5, sm: 2, md: 3 },
            pt: { xs: 1.5, sm: 2 },
            // Espaço para o botão flutuante do chat não cobrir o fim da página.
            pb: 11,
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          {semMarcaLiberada ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              Nenhuma marca liberada para o seu usuário ainda. Peça a um administrador para
              liberar o acesso.
            </Alert>
          ) : null}
          {marcasErro ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              Não foi possível carregar as marcas: {marcasErro}
            </Alert>
          ) : null}
          <Outlet />
        </Box>
        <ChatFlutuante />
      </Box>
    </Box>
  )
}
